export interface ThemeConfig {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  fontFamily: string;
  borderRadius: string;
  logoUrl?: string;
}

export interface ContentConfig {
  header: {
    title: string;
    subtitle: string;
  };
  steps: {
    [key: string]: {
      title: string;
      subtitle: string;
    }
  };
  labels: {
    [key: string]: string;
  };
  wizardSteps: string[];
  declaration: string;
}

export interface FormConfig {
  enable_employment: string; // '1' or '0'
  enable_referees: string;
  declaration_text: string;
  theme?: ThemeConfig;
  content?: ContentConfig;
  pmpro_map?: Record<string, number>;
  // Payment settings
  payment_gateway?: 'flutterwave' | 'paystack';
  payment_public_key?: string;
  payment_secret_key?: string;
  application_fee?: string; // stored as string, default '5000'
  bank_name?: string;
  bank_account_number?: string;
  bank_account_name?: string;
}

export enum MembershipCategory {
  FELLOW = 'Fellow',
  PROFESSIONAL = 'Professional Member',
  ASSOCIATE = 'Associate',
  LICENTIATE = 'Licentiate',
  STUDENT = 'Student Member'
}

export enum ApplicationType {
  UPGRADING = 'Upgrading',
  NEW = 'New Membership'
}

export enum Gender {
  MALE = 'Male',
  FEMALE = 'Female'
}

export enum MaritalStatus {
  SINGLE = 'Single',
  MARRIED = 'Married',
  WIDOW = 'Widow'
}

export interface AcademicEntry {
  id: string;
  name: string;
  institution: string;
  yearGraduated: string;
  certificate: string | null;
}

export interface EmploymentEntry {
  id: string;
  organization: string;
  fromMonth: string;
  fromYear: string;
  toMonth: string;
  toYear: string;
  position: string;
  department: string;
  salaryScale: string;
}

export interface Referee {
  name: string;
  address: string;
  signature: string | null;
  date: string;
  membershipId: string;
}

export interface RegistrationFormData {
  // Step 1
  membershipCategory: MembershipCategory | '';
  applicationType: ApplicationType | '';
  existingMembershipId: string; // Required for Fellow upgrade

  // Step 2
  surname: string;
  firstName: string;
  otherNames: string;
  dob: string;
  sex: Gender | '';
  maritalStatus: MaritalStatus | '';
  passportPhoto: string | null;

  // Step 3
  officeAddress: string;
  officeTelephone: string;
  officeEmail: string;
  homeAddress: string;
  postalAddress: string;
  mobileTelephone: string;
  homeEmail: string;

  // Step 4
  degrees: AcademicEntry[];
  diplomas: AcademicEntry[];

  // Step 5 (Student Specific)
  studentCourse: string;
  studentInstitution: string;
  studentCommenced: string;
  studentCompletion: string;
  studentMode: 'Full-Time' | 'Part-Time' | '';
  hodCertificate: string | null;
  hodSignature: string | null;

  // Step 6
  employmentHistory: string;

  // Step 7
  professionalBodies: string;

  // Step 8
  careerSummary: string;

  // Step 9
  referee1: Referee;
  referee2: Referee;
  chapterName: string;
  chapterDate: string;
  chapterChairman: string;

  // Step 10
  otherInfo: string;
  amountEnclosed: string;

  // Step 11
  declarationConfirmed: boolean;
  applicantSignature: string | null;
  submissionDate: string;

  // Step 12 - Payment
  paymentMethod: 'online' | 'offline' | '';
  paymentReference: string;
  paymentReceipt: string | null;
  paymentStatus: 'pending' | 'completed' | 'pending_verification' | '';
  
  // Step 13 - Fellow Specific
  fellowCitationUpload: string | null;
  fellowStatementUpload: string | null;
}

export interface Member {
  id: number;
  email: string;
  name: string;
  registered: string;
  data: RegistrationFormData;
  pmpro_level?: string;
}
